import { io, Socket } from "socket.io-client";
import { compararAcesso, lerAcesso, lerEsquema, ouvirAcesso, type AcessoNovo } from "@/lib/auth/acesso";
import { guardarNonce } from "@/lib/auth/dpop/prova";
import { bloquear, estaBloqueada, ouvirRedeBloqueada } from "@/lib/auth/redeBloqueada";
import { renovar } from "@/lib/auth/renovacao";
import { sairPorSessaoMorta } from "@/lib/auth/saida";
import { esperarFimDaOperacao } from "@/lib/auth/sessaoEntreAbas";
import { declaradoDoSocket } from "@/lib/declaradoDoCliente";
import { credencialDoSocket, guardarNonceDoAck, recusaDpopDo } from "@/lib/socketDpop";

let socket: Socket | null = null;

/**
 * Access que o ÚLTIMO handshake levou — gravado pelo callback `auth` na hora do connect. O corte compara com a
 * memória (D16): diferente = já há access novo (renovação, troca de senha nesta ou noutra aba).
 */
let tokenDoHandshake: string | null = null;

/** Pausa intencional (troca de senha, "Sair", "Sair de todos"): a queda nesse meio é esperada. */
let pausado = false;

/** Single-flight da reação ao corte: rajada de corte/recusa gera UMA renovação. */
let conferindo = false;

/** O `token_rejected` está renovando por HTTP pra reemitir: o ouvinte de access novo não emite junto. */
let reemitindo = false;

let desligarOuvinteDeAcesso: (() => void) | null = null;

let desligarOuvinteDeRede: (() => void) | null = null;

/** Handshake mais recente: a prova de um handshake velho que chega depois não manda CONNECT duplicado. */
let geracaoDoHandshake = 0;

/** O handshake já foi refeito por `use_dpop_nonce` (faixa 33 §6, B5): de novo → fluxo de corte. Zera no `connect`. */
let religouPorNonce = false;

/**
 * Esta instância já teve um `connect` aceito: o handshake seguinte é reconexão (faixa 39 §2, `reconexao` do `auth`).
 * Handshake repetido antes do 1º aceito (recusa, nonce, queda de transporte) ainda é o 1º connect. Zera com a instância.
 */
let conectouAntes = false;


/** Prazo do ack do `auth:renew` (faixa 25 §5). Rede de segurança: o back sempre responde ack, até com Redis fora. */
export const PRAZO_ACK_RENOVACAO_MS = 5_000;

export const connectSocket = (token: string): Socket => {
  if (!process.env.NEXT_PUBLIC_SOCKET_URL) {
    throw new Error("NEXT_PUBLIC_SOCKET_URL is not defined");
  }

  if (socket) {
    // Quem pausou é quem religa (`retomarSocket`).
    if (pausado) return socket;
    // Conectado com outro token: recicla a MESMA instância — o handshake novo lê a memória
    // pelo callback `auth`.
    if (socket.connected && tokenDoHandshake !== null && tokenDoHandshake !== token) {
      console.log("Token changed, forcing reconnection...");
      socket.disconnect();
      socket.connect();
      return socket;
    }
    // Parado (ex.: recusa anterior): acorda.
    if (!socket.active) socket.connect();
    return socket;
  }

  console.log("Iniciando nova instância do Socket...");
  conectouAntes = false;

  socket = io(process.env.NEXT_PUBLIC_SOCKET_URL, {
    path: "/socket.io/",
    transports: ["websocket", "polling"], // Prioritize websocket
    /*
      Callback, NUNCA objeto fixo (I5). O socket.io chama isto em TODO handshake — connect, reconexão do manager
      e `connect()` manual —, então toda conexão leva o access ATUAL da memória da aba (faixa 25 §5). Nunca um
      vencido: quem troca o access é a renovação, e ela avisa o socket (`aoAcessoNovo`).
    */
    auth: (cb) => {
      const token = lerAcesso();
      tokenDoHandshake = token;
      // Todo handshake avança a geração (inclusive o Bearer): prova velha que chega depois não manda CONNECT.
      const geracao = ++geracaoDoHandshake;
      // Faixa 39 §2: build/fuso/tela de AGORA + reconexao, só no connect (o `auth:renew` não leva).
      const declarado = declaradoDoSocket(conectouAntes);
      // Sessão DPoP (faixa 33 §6): o callback vira assíncrono — a prova é assinada a cada handshake (jti único).
      if (!token || lerEsquema() !== "DPoP") {
        cb({ token, ...declarado });
        return;
      }
      void credencialDoSocket(token).then((credencial) => {
        if (geracao === geracaoDoHandshake) cb({ ...credencial, ...declarado });
      });
    },
    autoConnect: true,
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    timeout: 20000,
    // Matching backend: ping_interval=25, ping_timeout=60
    // Socket.io client manages heartbeats automatically, but we ensure the connection is robust
    forceNew: false,
    closeOnBeforeunload: false, // Keep connection alive as long as possible
  });

  setupListeners(socket);
  desligarOuvinteDeAcesso = ouvirAcesso(aoAcessoNovo);
  desligarOuvinteDeRede = ouvirRedeBloqueada(aoMudarBloqueioDeRede);
  // Nasceu com o bloqueio aceso (o `io()` já pediu a conexão): pausa já — quem religa é o `liberar()`.
  if (estaBloqueada()) pausarSocket();

  return socket;
};

/**
 * Sessão do socket no back: o `connect` autentica o JWT e grava `sio:sid:{sid}` no Redis
 * (TTL 1h, NÃO renovada). Quando expira, os handlers de evento respondem com o EVENTO
 * `error` `{message: 'Session expired…'}` — o connect do back nunca recusa com essa string
 * (recusa = `return False` → connect_error genérico), então o antigo match de string no
 * connect_error era código morto. A recuperação certa: reciclar a conexão (disconnect +
 * connect), que re-autentica e recria a sessão.
 */
const SESSION_EXPIRED_MESSAGES = new Set([
  "Session expired or not found", // única string emitida pelo back (app/sockets/command.py:32)
  // Legado: os sockets de research que emitiam esta variante foram DELETADOS
  // (back 29792d18/3159d3d4). Mantida só por compatibilidade com back antigo em
  // ambiente defasado — nenhum código do back atual produz esta string.
  "Session expired",
]);

export const isSessionExpiredPayload = (payload: unknown): boolean =>
  typeof payload === "object" &&
  payload !== null &&
  "message" in payload &&
  SESSION_EXPIRED_MESSAGES.has(String((payload as { message: unknown }).message));

const RECYCLE_DELAY_MS = 1000;
const RETRY_BASE_DELAY_MS = 2000;
const RETRY_MAX_DELAY_MS = 30_000;

let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let retryAttempt = 0;

/**
 * Single-flight: N gatilhos em rajada (error de sessão, connect_error) geram UM
 * disparo agendado — sem hot-loop nem timers duelando.
 * - recycle: disconnect + connect (sessão expirada precisa re-autenticar do zero).
 * - retry: só connect, com backoff exponencial (2s → 30s) — é o resgate pós
 *   CONNECT_ERROR de namespace, quando o client destrói a subscription e NUNCA
 *   re-tentaria sozinho.
 */
const scheduleReconnect = (
  socketInstance: Socket,
  mode: "recycle" | "retry",
) => {
  if (reconnectTimer) return;
  const delay =
    mode === "recycle"
      ? RECYCLE_DELAY_MS
      : Math.min(RETRY_BASE_DELAY_MS * 2 ** retryAttempt, RETRY_MAX_DELAY_MS);
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    if (pausado) return;
    // Esta aba saiu nesse meio tempo (memória limpa) — a saída cuida do resto. O access em si não é
    // atribuído: o callback `auth` lê o atual no handshake.
    if (!lerAcesso()) return;
    if (mode === "recycle") {
      socketInstance.disconnect();
      socketInstance.connect();
      return;
    }
    retryAttempt += 1;
    // Se o socket voltou a ficar ativo (manager reconectou por conta própria),
    // connect() é no-op — chamada idempotente, sem conexão duplicada.
    socketInstance.connect();
  }, delay);
};

/**
 * O servidor cortou (`io server disconnect`) ou recusou o handshake (`connect_error` com o socket inativo).
 * Nenhum dos dois traz motivo: sessão morta OU a vigia cortando no `exp` de um access que o `auth:renew` não
 * alcançou. "Logout só com sessão morta" (faixa 22), e a conferência agora é a RENOVAÇÃO (D4 da faixa 25), que
 * de quebra traz o access novo:
 *  1. cancela re-tentativa pendente; 2. espera a operação anunciada (D9); 3. pausa ou instância trocada → nada;
 *  4. memória vazia → ESTA aba saiu: solta o socket; 5. access ≠ handshake → era o velho (D16): religa já;
 *  6. igual → renova (single-flight): `renovada`/`indisponivel` religam pelo BACKOFF (corte em sequência com
 *     sessão viva viraria laço quente connect → recusa → refresh); `sessao-morta`/`saida-pendente` → solta o
 *     socket e sai pela saída das abas abertas (I11).
 */
const aoSerCortado = async (socketInstance: Socket): Promise<void> => {
  if (pausado || conferindo) return;
  conferindo = true;
  try {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    await esperarFimDaOperacao();
    if (pausado || socket !== socketInstance) return;

    const usado = tokenDoHandshake;
    const antes = compararAcesso(usado);
    if (antes === "sumiu") {
      disconnectSocket();
      return;
    }
    if (antes === "mudou") {
      socketInstance.connect();
      return;
    }

    const resultado = await renovar({ acessoDoPedido: usado });
    if (pausado || socket !== socketInstance) return;
    // Rede fora da política (faixa 28): a renovação já acendeu o bloqueio, que pausa este socket. Nem backoff (o
    // handshake seria recusado de novo, em laço) nem saída (o cookie vale).
    if (resultado.tipo === "rede-bloqueada") return;
    if (resultado.tipo === "renovada" || resultado.tipo === "indisponivel") {
      scheduleReconnect(socketInstance, "retry");
      return;
    }
    disconnectSocket();
    sairPorSessaoMorta();
  } finally {
    conferindo = false;
  }
};

const CODIGOS_DO_RENEW = [
  "token_rejected",
  "session_expired",
  "invalid_payload",
  "internal",
  "network_forbidden",
  "use_dpop_nonce",
  "invalid_dpop_proof",
] as const;

type CodigoDoRenew = "ok" | (typeof CODIGOS_DO_RENEW)[number] | "desconhecido";

/** Lê o `code` do ack (`app/sockets/ack.py`: sucesso `{ok:true}`, recusa `{ok:false, code, message, nonce?}`). */
const codigoDoRenew = (ack: unknown): CodigoDoRenew => {
  if (typeof ack !== "object" || ack === null) return "desconhecido";
  const { ok, code } = ack as { ok?: unknown; code?: unknown };
  if (ok === true) return "ok";
  return CODIGOS_DO_RENEW.find((conhecido) => conhecido === code) ?? "desconhecido";
};


/**
 * `auth:renew {token}` (sessão DPoP: `{token, dpop}` com prova do token NOVO) com o access ATUAL (faixa 25 §5, I9):
 * troca as claims da vigia sem desconectar.
 * `ok` → nada · `use_dpop_nonce`/`invalid_dpop_proof` (nonce do ack guardado) → reemite UMA vez com prova nova;
 * segunda recusa DPoP → recicla (orçamento herdado pela reemissão do `token_rejected`) ·
 * `token_rejected` → renova por HTTP e reemite UMA vez; recusado de novo → recicla ·
 * `session_expired` → recicla · `network_forbidden` → `bloquear()`, sem reciclar · `invalid_payload` → bug: loga e recicla · `internal` (Redis fora no back: ack
 * `{ok:false, code:'internal'}`, back `22628c3e`), desconhecido ou sem ack no prazo (rede de segurança) → nada;
 * a próxima renovação tenta, e o corte, se vier, renova.
 */
const renovarNoSocket = async (segunda: boolean, reemitidoPorDpop = false): Promise<void> => {
  const instancia = socket;
  const token = lerAcesso();
  if (!instancia || !instancia.connected || pausado || !token) return;
  // Sessão DPoP: `{token, dpop}` com prova do token NOVO (faixa 33 §6). Bearer segue síncrono, como antes.
  const credencial = lerEsquema() === "DPoP" ? await credencialDoSocket(token) : { token };
  if (socket !== instancia || pausado) return;
  let ack: unknown;
  try {
    ack = await instancia.timeout(PRAZO_ACK_RENOVACAO_MS).emitWithAck("auth:renew", credencial);
  } catch {
    return; // sem ack no prazo, ou o socket caiu no meio
  }
  if (socket !== instancia || pausado) return;
  guardarNonceDoAck(ack);
  const codigo = codigoDoRenew(ack);
  // Recusa DPoP (nonce vencido ou prova ruim): reemite UMA vez com prova nova; a segunda recicla, como session_expired.
  if ((codigo === "use_dpop_nonce" || codigo === "invalid_dpop_proof") && !reemitidoPorDpop) {
    await renovarNoSocket(segunda, true);
    return;
  }
  if (codigo === "network_forbidden") {
    // Faixa 28: IP fora da rede permitida. Acende o bloqueio (que pausa este socket) e NÃO recicla — reconectar seria
    // recusado. Quem religa é o `liberar()`.
    bloquear();
    return;
  }
  if (codigo === "ok" || codigo === "internal" || codigo === "desconhecido") return;
  if (codigo === "token_rejected" && !segunda) {
    reemitindo = true;
    let resultado: Awaited<ReturnType<typeof renovar>>;
    try {
      resultado = await renovar();
    } finally {
      reemitindo = false;
    }
    if (socket !== instancia || pausado) return;
    if (resultado.tipo === "renovada") {
      await renovarNoSocket(true, reemitidoPorDpop);
      return;
    }
    if (resultado.tipo === "indisponivel" || resultado.tipo === "rede-bloqueada") return;
    disconnectSocket();
    sairPorSessaoMorta();
    return;
  }
  if (codigo === "invalid_payload") console.error("auth:renew recusado por payload inválido — bug do front:", ack);
  scheduleReconnect(instancia, "recycle");
};

/**
 * Todo access novo de RENOVAÇÃO (desta aba ou entregue por outra) renova o socket. O da troca de senha é outro
 * `sid`: quem reconecta é o `retomarSocket` (esta aba) ou o corte com access ≠ handshake (as outras) — §7.
 */
const aoAcessoNovo = (novo: AcessoNovo): void => {
  if (novo.origem !== "renovacao" || reemitindo) return;
  void renovarNoSocket(false);
};

// Função auxiliar para organizar os listeners
const setupListeners = (socketInstance: Socket) => {
  socketInstance.on("connect", () => {
    retryAttempt = 0; // conexão aceita — backoff volta ao delay base
    religouPorNonce = false;
    conectouAntes = true;
    console.log("✅ Conectado! ID:", socketInstance.id);
  });

  socketInstance.on("connect_error", (err) => {
    console.log("❌ Erro de conexão:", err.message);
    // Recusa DPoP (faixa 33 §6): o `nonce` vem sempre novo — guarda. `use_dpop_nonce` religa UMA vez na hora, fora do
    // backoff (o handshake seguinte assina com o nonce novo); de novo, ou `invalid_dpop_proof`/`invalid_token`, é o
    // corte de sempre (renova; renovação recusada → login).
    const recusa = recusaDpopDo(err);
    if (recusa?.nonce) guardarNonce(recusa.nonce);
    if (recusa?.code === "use_dpop_nonce" && !religouPorNonce && !pausado && socket === socketInstance) {
      religouPorNonce = true;
      socketInstance.connect();
      return;
    }
    // Rejeição de NAMESPACE (back devolveu False no connect: token morto, Redis instável,
    // deploy no meio): o socket.io-client destrói a subscription (active=false) e não
    // re-tenta sozinho. Antes de re-tentar, confere se o acesso ainda vale — sem isso,
    // token revogado virava laço de recusa até o F5. Erro de transporte (active=true)
    // fica com o manager, que já re-tenta com reconnectionAttempts: Infinity.
    if (!socketInstance.active) void aoSerCortado(socketInstance);
  });

  socketInstance.on("disconnect", (reason) => {
    console.log(`⚠️ Desconectado. Motivo: ${reason}`);
    // `io server disconnect` vem sem motivo: token morto OU vigia com token válido.
    // Conferir antes de sair (D4). Queda de transporte ('transport close') o socket.io
    // re-tenta sozinho; 'io client disconnect' é nossa (pausa/logout).
    if (reason === "io server disconnect") void aoSerCortado(socketInstance);
  });

  socketInstance.on("error", (error) => {
    if (isSessionExpiredPayload(error)) {
      console.log("⚠️ Sessão do socket expirada no back. Reciclando conexão…");
      scheduleReconnect(socketInstance, "recycle");
      return;
    }
    console.log("❌ Erro genérico no socket:", error);
  });

  // Debug de tentativas de reconexão
  socketInstance.io.on("reconnect_attempt", (attempt) => {
    console.log(`🔄 Tentativa de reconexão #${attempt}`);
  });
};

/**
 * Bloqueio por rede (faixa 28 §4): aceso → pausa a MESMA instância (D3: `disconnectSocket` soltaria a instância e o
 * `SocketProvider` não religaria); apagado → religa. Enquanto aceso, corte, recusa e backoff ficam mudos pela pausa.
 */
const aoMudarBloqueioDeRede = (): void => {
  if (estaBloqueada()) pausarSocket();
  else retomarSocket();
};

export const getSocket = (): Socket | null => socket;

/**
 * Pausa intencional: desliga a conexão SEM trocar a instância. O `SocketProvider` conecta
 * uma vez e guarda ESTA instância no estado (`SocketContext.tsx`, effect de mount) — uma
 * nova nunca chegaria aos consumidores (I5).
 */
export const pausarSocket = (): void => {
  pausado = true;
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  socket?.disconnect();
};

/**
 * Religa a MESMA instância; o handshake lê o access atual da memória pelo callback `auth`. Com o bloqueio por rede
 * aceso (ex.: fim da troca de senha que levou o 403) fica pausado — quem religa é o `liberar()`.
 */
export const retomarSocket = (): void => {
  if (estaBloqueada()) return;
  pausado = false;
  socket?.connect();
};

/**
 * A volta da aba (`visibilitychange` dos providers): religa a instância caída, a MESMA do `SocketProvider` (I5).
 * Ponto central pra não furar a pausa: pausado (troca de senha, saída) ou com o bloqueio por rede aceso, NÃO religa —
 * quem religa é quem pausou (`retomarSocket`/`liberar()`, faixa 28 inv. 6b). Sem instância ou já conectada: nada.
 */
export const acordarSocket = (alvo: Socket | null): void => {
  if (!alvo || alvo.connected) return;
  if (pausado || estaBloqueada()) return;
  alvo.connect();
};

/** Encerramento (logout, sessão morta): limpa timer, backoff, pausa e handshake, e solta a instância. */
export const disconnectSocket = () => {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  retryAttempt = 0;
  pausado = false;
  reemitindo = false;
  religouPorNonce = false;
  tokenDoHandshake = null;
  desligarOuvinteDeAcesso?.();
  desligarOuvinteDeAcesso = null;
  desligarOuvinteDeRede?.();
  desligarOuvinteDeRede = null;
  if (socket) {
    socket.disconnect();
    socket = null; // AQUI sim podemos limpar, pois o usuário quis sair
    console.log("Socket desconectado e limpo manualmente.");
  }
};
